import type { ContentItemType } from '../generated/social-media/metapicSocialMediaAPIDocumentation.schemas'
import {
  ALL_CONTENT_TYPES,
  PLATFORM_CONTENT_TYPES,
  SUPPORTED_CONTENT_ITEM_PLATFORMS,
  type SupportedContentItemPlatform,
} from '../types/content-item-platform.types'

export const platformToContentTypes = (
  platform: SupportedContentItemPlatform,
): ContentItemType[] => [...PLATFORM_CONTENT_TYPES[platform]]

const CONTENT_TYPE_TO_PLATFORM = new Map<string, SupportedContentItemPlatform>(
  SUPPORTED_CONTENT_ITEM_PLATFORMS.flatMap((platform) =>
    (PLATFORM_CONTENT_TYPES[platform] as readonly ContentItemType[]).map(
      (type) => [type, platform] as const,
    ),
  ),
)

export const isSupportedContentItemPlatform = (
  value: string,
): value is SupportedContentItemPlatform =>
  (SUPPORTED_CONTENT_ITEM_PLATFORMS as readonly string[]).includes(value)

export const platformOfContentType = (value: string): SupportedContentItemPlatform | undefined =>
  CONTENT_TYPE_TO_PLATFORM.get(value)

/** `undefined` when `types` is empty, holds an unknown value, or spans platforms. */
export const platformOfContentTypes = (
  types: readonly string[] | undefined,
): SupportedContentItemPlatform | undefined => {
  const [first, ...rest] = types ?? []
  if (first === undefined) {
    return undefined
  }

  const platform = platformOfContentType(first)
  if (!platform) {
    return undefined
  }

  return rest.every((type) => platformOfContentType(type) === platform) ? platform : undefined
}

/** Reorders `types` canonically, dropping duplicates and unknown values. */
export const orderContentTypes = (types: readonly string[]): ContentItemType[] => {
  const picked = new Set(types)
  return ALL_CONTENT_TYPES.filter((type) => picked.has(type))
}
